import { useState } from "react";
import { trackEvent } from "@/lib/analytics";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";

const CONTACT_TYPES = [
  { id: "client", label: "Client" },
  { id: "prospect", label: "Prospect" },
  { id: "vendor", label: "Vendor" },
  { id: "fund_manager", label: "Fund Manager (GP)" },
  { id: "lp", label: "LP" },
  { id: "advisor", label: "Advisor" },
  { id: "founder", label: "Founder" },
  { id: "service_provider", label: "Service Provider" },
] as const;

const contactSchema = z.object({
  full_name: z.string().min(1, "Name is required").max(100),
  email: z.string().email("Invalid email").max(255).optional().or(z.literal("")),
  phone: z.string().max(50).optional().or(z.literal("")),
  role: z.string().optional().or(z.literal("")),
  organization: z.string().max(200).optional().or(z.literal("")),
  warmth: z.number().min(0).max(5),
  contact_types: z.array(z.string()).default([]),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface CreateContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateContactDialog({ open, onOpenChange, onSuccess }: CreateContactDialogProps) {
  const { toast } = useToast();
  const { orgId, familyId } = useTenant();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      role: "",
      organization: "",
      warmth: 3,
      contact_types: [],
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      if (!orgId) {
        throw new Error("Organization not loaded yet. Please try again.");
      }

      // Get the first client for this org/family
      const { data: clients } = await supabase
        .from("clients")
        .select("id")
        .eq("org_id", orgId)
        .limit(1)
        .single();

      if (!clients) {
        throw new Error("No client record found for this organization");
      }

      const { error } = await supabase.from("contacts").insert({
        full_name: data.full_name,
        email: data.email || null,
        phone: data.phone || null,
        role: data.role || null,
        organization: data.organization || null,
        warmth: data.warmth,
        contact_types: data.contact_types,
        client_id: clients.id,
        org_id: orgId,
        family_id: familyId,
        last_interaction: new Date().toISOString(),
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Contact created successfully",
      });

      form.reset();
      onOpenChange(false);
      onSuccess();
      trackEvent('contact_created');
    } catch (err) {
      console.error("Error creating contact:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to create contact",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
          <DialogDescription>
            Create a new contact for your CRM
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="+1 (555) 123-4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Principal">Principal</SelectItem>
                      <SelectItem value="CIO">CIO</SelectItem>
                      <SelectItem value="GP">GP</SelectItem>
                      <SelectItem value="Advisor">Advisor</SelectItem>
                      <SelectItem value="Service Provider">Service Provider</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organization"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organization</FormLabel>
                  <FormControl>
                    <Input placeholder="Company name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contact_types"
              render={() => (
                <FormItem>
                  <div className="mb-2">
                    <FormLabel>Contact Types</FormLabel>
                    <FormDescription>
                      Select all types that apply to this contact
                    </FormDescription>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {CONTACT_TYPES.map((type) => (
                      <FormField
                        key={type.id}
                        control={form.control}
                        name="contact_types"
                        render={({ field }) => (
                          <FormItem
                            key={type.id}
                            className="flex flex-row items-start space-x-2 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(type.id)}
                                onCheckedChange={(checked) => {
                                  const currentValues = field.value || [];
                                  if (checked) {
                                    field.onChange([...currentValues, type.id]);
                                  } else {
                                    field.onChange(
                                      currentValues.filter((value) => value !== type.id)
                                    );
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {type.label}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="warmth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Warmth (0-5)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      max="5" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Contact"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
